import type {
    IConnectionsResponse,
    IConnectionStatus,
    IEmailConfiguration,
    IFolderUpdatePayload,
    INotificationProfileResponse,
    IOrchestratorFolderEntity,
    IProfileUpdateSubscriptionPayload,
    ISlackChannel,
    ISlackConnectionConfiguration,
    ITeamsChannel,
    ITeamsConnectionConfiguration,
    ITeamsTeam,
    IUpdateCategorySubscriptionModeRequestData,
    IUpdateUserSubscriptionsRequestData,
} from '../../component/notificationSettings/interfaces/notificationSettings';
import {
    axiosDelete,
    get,
    post,
} from '../utility/Requests';

const basePath = '/notificationservice_/usersubscriptionservice';
const initialApiPath = basePath + '/api/v1';
const initialUserSubscriptionApiPath = initialApiPath + '/UserSubscription';
const initialOdataPath = basePath + '/odata/v1';

const getApiUrl = (prtId: string, requestUri: string) => prtId + requestUri;

export const userNotificationSubscriptionUri = initialUserSubscriptionApiPath + '/';

const resetUserNotificationSeveritySubscriptionUri = initialUserSubscriptionApiPath + '/CategorySubscription';
export const SubscriptionProfileMetadata = initialOdataPath + '/SubscriptionProfileMetadata';

const UnsubscribeTenant = initialUserSubscriptionApiPath + '/UnsubscribeTenant';
export const UpdateDefaultSubscriptionUrl = initialUserSubscriptionApiPath + '/UpdateDefaultSubscription';
const resetUserNotificationPublisherSubscriptionUri = initialUserSubscriptionApiPath + '/PublisherSubscription';
const resetUserNotificationSubscriptionUri = initialUserSubscriptionApiPath + '/reset';
export const folderNavigationUrl = '/api/notificationSettings/folderNavigation';

export const emailConfigurationUrl = initialApiPath + '/emailConfiguration';
export const SubscriptionProfile = initialApiPath + '/SubscriptionProfile';
const ConnectionProxy = initialApiPath + '/ConnectionProxy/Get?ConnectorType=';
const ConnectionProxySlack = ConnectionProxy + 'Slack';
const ConnectionProxyTeams = ConnectionProxy + 'Teams';
const ConnectionChannelsProxy = initialApiPath + '/ConnectionProxy/GetChannels';
const ConnectionTeamsProxy = initialApiPath + '/ConnectionProxy/GetTeams';
const ConnectionTeamsChannelsProxy = initialApiPath + '/ConnectionProxy/GetTeamsChannels';
export const slackConfigurationUrl = initialApiPath + '/ConnectionAffiliates';
export const teamsConfigurationUrl = initialApiPath + '/ConnectionAffiliates';
const ConnectionAffiliatesBase = initialApiPath + '/ConnectionAffiliates/Get?ConnectorType=';
export const ConnectionAffiliates = ConnectionAffiliatesBase + 'Slack';
export const ConnectionAffiliatesTeams = ConnectionAffiliatesBase + 'Teams';

const connectionBasePath = 'connections_/api/v1/Connections/';
const getConnectionApiUrl = (prtId: string, tenantId: string, requestUri: string) => `${prtId}/${tenantId}/${requestUri}`;

const getHeaders = (selectedTenantId: string) => ({
    extendRequestHeaders: 'true',
    'X-UiPath-Internal-TenantId': selectedTenantId,
});
export function updateSlackConfiguration(requestUri: string,
    updateEmailConfigurationData: ISlackConnectionConfiguration,
    selectedTenantId: string, selectedAccountId: string) {
    return post<string>(getApiUrl(selectedAccountId, requestUri), {
        ignoreBasePath: true,
        body: { ...updateEmailConfigurationData },
        headers: getHeaders(selectedTenantId),
    });
}

export function updateTeamsConfiguration(requestUri: string,
    updateEmailConfigurationData: ITeamsConnectionConfiguration,
    selectedTenantId: string, selectedAccountId: string) {
    return post<string>(getApiUrl(selectedAccountId, requestUri), {
        ignoreBasePath: true,
        body: { ...updateEmailConfigurationData },
        headers: getHeaders(selectedTenantId),
    });
}

export function getSlackChannels({
    SupplementConnectionId, startsWith, selectedTenantId, selectedAccountId,
}: { SupplementConnectionId: String;
    startsWith: string; selectedTenantId: string; selectedAccountId: string; }) {

    return get<ISlackChannel[]>(getApiUrl(selectedAccountId, ConnectionChannelsProxy), {
        ignoreBasePath: true,
        urlParams: {
            SupplementConnectionId,
            startsWith,
        },
        headers: getHeaders(selectedTenantId),
    });
}

export function getTeamsChannels({
    SupplementConnectionId, TeamId, startsWith, selectedTenantId, selectedAccountId,
}: { SupplementConnectionId: String; TeamId: string;
    startsWith: string; selectedTenantId: string; selectedAccountId: string; }) {

    return get<ITeamsChannel[]>(getApiUrl(selectedAccountId, ConnectionTeamsChannelsProxy), {
        ignoreBasePath: true,
        urlParams: {
            SupplementConnectionId,
            TeamId,
            startsWith,
        },
        headers: getHeaders(selectedTenantId),
    });
}

export function getTeams({
    SupplementConnectionId, startsWith, selectedTenantId, selectedAccountId,
}: { SupplementConnectionId: String;
    startsWith: string; selectedTenantId: string; selectedAccountId: string; }) {

    return get<ITeamsTeam[]>(getApiUrl(selectedAccountId, ConnectionTeamsProxy), {
        ignoreBasePath: true,
        urlParams: {
            SupplementConnectionId,
            startsWith,
        },
        headers: getHeaders(selectedTenantId),
    });
}

export function getConnectionStatus({
    selectedConnectionId, selectedTenantId, selectedAccountId,
}: { selectedConnectionId: string; selectedTenantId: string; selectedAccountId: string }) {
    return get<IConnectionStatus>(getConnectionApiUrl(selectedAccountId, selectedTenantId, connectionBasePath + selectedConnectionId + '/ping?forceRefresh=true'), {
        ignoreBasePath: true,
        headers: getHeaders(selectedTenantId),
    });
}

export function getConnections({
    selectedTenantId, selectedAccountId,
}: { selectedTenantId: string; selectedAccountId: string }) {
    return get<IConnectionsResponse>(getApiUrl(selectedAccountId, ConnectionProxySlack), {
        ignoreBasePath: true,
        headers: getHeaders(selectedTenantId),
    });
}

export function getTeamsConnections({
    selectedTenantId, selectedAccountId,
}: { selectedTenantId: string; selectedAccountId: string }) {
    return get<IConnectionsResponse>(getApiUrl(selectedAccountId, ConnectionProxyTeams), {
        ignoreBasePath: true,
        headers: getHeaders(selectedTenantId),
    });
}

export function getAffiliatedConnection({
    requestUri, selectedTenantId, selectedAccountId,
}: { requestUri: string; selectedTenantId: string; selectedAccountId: string }) {
    return get<ISlackConnectionConfiguration>(getApiUrl(selectedAccountId, requestUri), {
        ignoreBasePath: true,
        headers: getHeaders(selectedTenantId),
    });
}

export function getTeamsAffiliatedConnection({
    requestUri, selectedTenantId, selectedAccountId,
}: { requestUri: string; selectedTenantId: string; selectedAccountId: string }) {
    return get<ITeamsConnectionConfiguration>(getApiUrl(selectedAccountId, requestUri), {
        ignoreBasePath: true,
        headers: getHeaders(selectedTenantId),
    });
}

export function getEmailConfiguration({
    requestUri, selectedTenantId, selectedAccountId,
}: { requestUri: string; selectedTenantId: string; selectedAccountId: string }) {
    return get<IEmailConfiguration>(getApiUrl(selectedAccountId, requestUri), {
        ignoreBasePath: true,
        headers: getHeaders(selectedTenantId),
    });
}

export function resetEmailConfiguration(requestUri: string, selectedTenantId: string, selectedAccountId: string) {
    return axiosDelete(getApiUrl(selectedAccountId, requestUri), {
        ignoreBasePath: true,
        headers: getHeaders(selectedTenantId),
    });
}

export function updateEmailConfiguration(requestUri: string,
    updateEmailConfigurationData: IEmailConfiguration,
    selectedTenantId: string, selectedAccountId: string) {
    return post<string>(getApiUrl(selectedAccountId, requestUri), {
        ignoreBasePath: true,
        body: { ...updateEmailConfigurationData },
        headers: getHeaders(selectedTenantId),
    });
}

export function updateDefaultSubscription({
    requestUri, selectedTenantId, selectedAccountId,
}: { requestUri: string; selectedTenantId: string; selectedAccountId: string }) {
    return post<string>(getApiUrl(selectedAccountId, requestUri), {
        ignoreBasePath: true,
        body: { TenantId: selectedTenantId },
        headers: getHeaders(selectedTenantId),
    });
}

export function getFolderStructure({
    requestUri, selectedTenantId, urlTemplate, accountName, tenantName,
}: { requestUri: string; selectedTenantId: string; urlTemplate: string;
    accountName: string; tenantName: string; }) {
    urlTemplate = urlTemplate.replace('{accountName}', accountName);
    urlTemplate = urlTemplate.replace('{tenantName}', tenantName);
    const headers = getHeaders(selectedTenantId);
    return get<IOrchestratorFolderEntity[]>(urlTemplate, {
        ignoreBasePath: true,
        headers,
    });
}

export function updateUserPublisherSubscriptionSubscription(
    updateUserSubscriptions: IFolderUpdatePayload,
    selectedTenantId: string,
    selectedAccountId: string) {
    return post<any>(getApiUrl(selectedAccountId, resetUserNotificationPublisherSubscriptionUri), {
        ignoreBasePath: true,
        body: { publisherSubscriptions: [ updateUserSubscriptions ] },
        headers: getHeaders(selectedTenantId),
    });
}

export function deleteProfile(profileName: string, selectedTenantId: string, selectedAccountId: string) {
    return axiosDelete(getApiUrl(selectedAccountId, SubscriptionProfile + '/' + profileName), {
        ignoreBasePath: true,
        headers: getHeaders(selectedTenantId),
    });
}

export function getSubscriptionProfile(requestUri: string, selectedTenantId: string, selectedAccountId: string) {

    return get<any>(getApiUrl(selectedAccountId, requestUri), {
        ignoreBasePath: true,
        headers: getHeaders(selectedTenantId),
    });
}

export function getSubscriptionProfiles({
    requestUri, selectedTenantId, selectedAccountId,
}: { requestUri: string; selectedTenantId: string;selectedAccountId: string }) {
    return get<INotificationProfileResponse>(getApiUrl(selectedAccountId, requestUri), {
        ignoreBasePath: true,
        headers: getHeaders(selectedTenantId),
    });
}

export function getUserNotificationSubscription({
    requestUri, selectedTenantId, selectedAccountId, publisherName,
}: { requestUri: string; selectedTenantId: string; selectedAccountId: string; publisherName?: string }) {
    return get<any>(getApiUrl(selectedAccountId, requestUri), {
        ignoreBasePath: true,
        headers: getHeaders(selectedTenantId),
        urlParams: { publisherName },
    });
}

export function updateProfileNotificationSubscription(
    updateUserSubscriptions: IProfileUpdateSubscriptionPayload,
    selectedTenantId: string,
    selectedAccountId: string) {
    return post<any>(getApiUrl(selectedAccountId, SubscriptionProfile), {
        ignoreBasePath: true,
        body: { ...updateUserSubscriptions },
        headers: getHeaders(selectedTenantId),
    });
}

export function updateUserNotificationTopicSubscription(
    updateUserSubscriptions: IUpdateUserSubscriptionsRequestData[],
    selectedTenantId: string,
    selectedAccountId: string) {
    return post<any>(getApiUrl(selectedAccountId, userNotificationSubscriptionUri), {
        ignoreBasePath: true,
        body: { userSubscriptions: [ ...updateUserSubscriptions ] },
        headers: getHeaders(selectedTenantId),
    });
}

export function updateUserNotificationSeveritySubscriptionMode(
    updateNotificationMode: IUpdateCategorySubscriptionModeRequestData[],
    selectedTenantId: string,
    selectedAccountId: string) {
    return post<any>(getApiUrl(selectedAccountId, resetUserNotificationSeveritySubscriptionUri), {
        ignoreBasePath: true,
        body: { CategorySubscriptions: [ ...updateNotificationMode ] },
        headers: getHeaders(selectedTenantId),
    });
}

export async function resetUserNotificationSubscription(publisherId: string, selectedTenantId: string, selectedAccountId: string) {
    return await post<any>(getApiUrl(selectedAccountId, resetUserNotificationSubscriptionUri), {
        ignoreBasePath: true,
        body: { publisherId },
        headers: getHeaders(selectedTenantId),
    });
}

export async function UnsubscribeTenantNotificationSubscription(selectedTenantId: string, selectedAccountId: string) {
    return await post<string>(getApiUrl(selectedAccountId, UnsubscribeTenant), {
        ignoreBasePath: true,
        body: { tenantId: selectedTenantId },
        headers: getHeaders(selectedTenantId),
    });
}
